import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import ParagraphTag from "../../reusebale/Paragraph";
import MyLoader from "../../reusebale/myLoader";
import { Check } from "lucide-react";
import Breadcrumbs from "../../reusebale/breadcrumbs";
import BaseUrl from "../../../constants/BaseUrl";
import { ShowError, ShowSuccess } from "../../../utils/ToasFunction";
import * as AuthAction from "../../../store/actions/auth/AuthAction";

const SubscriptionPlan = ({ onBack, allowSelect = false }) => {
  const { loginSuccess, userCompleteProfile } = useSelector(
    (state) => state.auth
  );

  const dispatch = useDispatch();

  const [billingCycle, setBillingCycle] = useState("monthly"); // 'monthly' or 'annual'
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purchaseLoadingId, setPurchaseLoadingId] = useState(null);
  const [confirmPackage, setConfirmPackage] = useState(null); // { apiPackage, displayPackage, customPropertyCount, customPricing }
  const [customProperties, setCustomProperties] = useState({}); // Track custom property counts for each custom package

  // Fetch packages on component mount
  useEffect(() => {
    const fetchPackages = () => {
      setLoading(true);
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      fetch(`${BaseUrl}api/payment/getAll/package`, requestOptions)
        .then((response) => response.json())
        .then((result) => {
          if (result.status === "success") {
            setPackages(result.data);
            setLoading(false);
          } else {
            setLoading(false);
            ShowError(result.message || "Failed to fetch packages");
          }
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          ShowError("Failed to fetch packages");
        });
    };

    fetchPackages();
  }, [loginSuccess?.token]);

  const buySubscription = (packageId, planName) => {
    setPurchaseLoadingId(packageId);

    // Calculate dates
    const startDate = new Date();
    const endDate = new Date();

    // Find the selected package to check duration
    const selectedPackage = packages.find((pkg) => pkg._id === packageId);
    if (selectedPackage?.numberOfMonth) {
      // Add the duration in months from the API
      endDate.setMonth(endDate.getMonth() + selectedPackage.numberOfMonth);
    } else {
      // Fallback to 6 months if no duration specified
      endDate.setMonth(endDate.getMonth() + 6);
    }

    // Prepare the package object with required fields only
    const packageData = {
      _id: selectedPackage._id,
      planName: selectedPackage.planName,
      numberOfMonth: selectedPackage.numberOfMonth,
      totalPackagePrice: selectedPackage.totalPackagePrice,
      monthlyPrice: selectedPackage.monthlyPrice,
      pricePerProperty: selectedPackage.pricePerProperty,
      isAnnual: selectedPackage.isAnnual,
      isCustom: selectedPackage.isCustom,
      allowProperties: selectedPackage.allowProperties,
      validations: selectedPackage.validations,
      discount: selectedPackage.discount || 0,
    };

    // Add custom property count if it's a custom package
    if (selectedPackage.isCustom) {
      const customPropertyCount = customProperties[packageId] || 0;
      const customPricing = calculateCustomPrice(
        selectedPackage,
        customPropertyCount
      );

      packageData.customPropertyCount = customPropertyCount;
      // Recalculate pricing for custom package with discount
      if (customPropertyCount > 0) {
        packageData.totalPackagePrice = customPricing.finalTotalPrice;
        packageData.monthlyPrice = customPricing.finalMonthlyPrice;
        packageData.allowProperties = customPropertyCount;
        packageData.discountPercentage = customPricing.discountPercentage;
        packageData.discountAmount = customPricing.discountAmount;
        packageData.originalPrice = customPricing.originalPrice;
      }
    }

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const raw = JSON.stringify({
      landloardId: loginSuccess?.user?._id,
      createSubscription: true,
      SubscriptionPackage: {
        packageId: packageId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        packageData: packageData,
      },
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(`${BaseUrl}landlord/profile/buySubscription`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "Success") {
          setPurchaseLoadingId(null);
          setConfirmPackage(null);
          ShowSuccess(`${planName} subscription purchased successfully!`);
          dispatch(
            AuthAction.GetLandlordProfile(
              loginSuccess?.user?._id,
              loginSuccess.token
            )
          );
          onBack();
        } else {
          setPurchaseLoadingId(null);
          ShowError(result.message || "Failed to purchase subscription");
        }
      })
      .catch((error) => {
        console.error(error);
        setPurchaseLoadingId(null);
        ShowError("Failed to purchase subscription");
      });
  };

  // Check if user can select a subscription plan
  const canSelectPlan = () => {
    // allowSelect bypasses all checks (used from dashboard subscription tile)
    if (allowSelect) return true;
    if (!userCompleteProfile?.personalInformation) return false;
    if (!userCompleteProfile?.companyInformation) return false;
    if (userCompleteProfile?.subscriptionPackage?.length) return false;
    return true;
  };

  // Filter packages based on billing cycle
  const getFilteredPackages = () => {
    const isAnnual = billingCycle === "annual";
    return packages.filter((pkg) => pkg.isAnnual === isAnnual);
  };

  const mapPackageToDisplay = (apiPackage) => {
    return {
      _id: apiPackage._id,
      package: apiPackage.planName,
      totalPrice: `$${apiPackage.totalPackagePrice}`,
      monthlyPrice: `$${Math.round(apiPackage.monthlyPrice)}`,
      savePerYear: apiPackage.savePrice > 0 ? `$${apiPackage.savePrice}` : null,
      description: apiPackage.description,
      durationLabel: apiPackage.isAnnual ? "12-Month Total" : "6-Month Total",
      features: apiPackage.allowFeatures || [],
      allowProperties: apiPackage.allowProperties,
      isCustom: apiPackage.isCustom,
      pricePerProperty: apiPackage.pricePerProperty,
      numberOfMonth: apiPackage.numberOfMonth,
    };
  };

  const calculateCustomPrice = (apiPackage, propertyCount) => {
    if (!propertyCount || propertyCount <= 0) {
      return {
        totalPrice: "$0",
        monthlyPrice: "$0",
        discountPercentage: 0,
        discountAmount: "$0",
        originalPrice: "$0",
      };
    }

    const originalPrice = propertyCount * apiPackage.pricePerProperty;

    let discountPercentage = 0;
    if (propertyCount >= 25) {
      discountPercentage = 20;
    } else if (propertyCount >= 10) {
      discountPercentage = 10;
    }

    const discountAmount = Math.round(
      (originalPrice * discountPercentage) / 100
    );
    const totalPrice = originalPrice - discountAmount;
    const monthlyPrice = Math.round(totalPrice / apiPackage.numberOfMonth);

    return {
      totalPrice: `$${totalPrice.toLocaleString()}`,
      monthlyPrice: `$${monthlyPrice.toLocaleString()}`,
      discountPercentage,
      discountAmount: `$${discountAmount.toLocaleString()}`,
      originalPrice: `$${originalPrice.toLocaleString()}`,
      finalTotalPrice: totalPrice,
      finalMonthlyPrice: monthlyPrice,
    };
  };

  useEffect(() => {
    if (userCompleteProfile?.subscriptionPackage?.length > 0) {
      setBillingCycle(
        userCompleteProfile.subscriptionPackage[0].packageData.isAnnual
          ? "annual"
          : "monthly"
      );
      if (
        userCompleteProfile?.subscriptionPackage[0]?.packageData?.isCustom ===
        true
      ) {
        setCustomProperties((prev) => ({
          ...prev,
          [userCompleteProfile.subscriptionPackage[0]._id]:
            userCompleteProfile.subscriptionPackage[0].packageData
              .customPropertyCount || 0,
        }));
      }
    }
  }, [userCompleteProfile?.subscriptionPackage]);

  return (
    <div className="w-full flex items-start px-4 sm:px-6 lg:px-8">
      <div className="w-[95%] mx-auto pt-4">
        {/* Header Section */}
        <div className="pb-4">
          <div className="flex items-start justify-start pb-8 pt-4">
            <div className="w-full">
              <Breadcrumbs
                recordId={`${loginSuccess?.user?.firstName} ${loginSuccess?.user?.lastName}`}
                subheading="My Account"
                onBack={onBack}
              />
            </div>
          </div>

          <div className="w-full flex items-center justify-center">
            <div className="text-center">
              <ParagraphTag
                content="Subscription Plan"
                classes="text-darkColor font-popinsSemiBold text-xl lg:text-2xl"
              />
            </div>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 rounded-lg p-1 flex">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-2  text-sm font-popinsMedium transition-all duration-300 ${
                billingCycle === "monthly"
                  ? "bg-themeColor text-white"
                  : "text-darkColor hover:text-themeColor bg-white"
              }`}
            >
              6 Months
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-4 py-2  text-sm font-popinsMedium transition-all duration-300 ${
                billingCycle === "annual"
                  ? "bg-themeColor text-white"
                  : "text-darkColor hover:text-themeColor bg-white"
              }`}
            >
              Annual
            </button>
          </div>
        </div>

        {/* Subscription Cards */}
        <div className="relative mb-8">
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 transition-opacity duration-300 ${confirmPackage ? "opacity-20 pointer-events-none select-none" : ""}`}>
          {loading ? (
            <div className="col-span-full flex justify-center items-center py-8">
              <MyLoader />
            </div>
          ) : (
            getFilteredPackages().map((apiPackage) => {
              const displayPackage = mapPackageToDisplay(apiPackage);
              const isCustom = displayPackage.isCustom;
              const customPropertyCount = customProperties[apiPackage._id] || 0;
              const customPricing = isCustom
                ? calculateCustomPrice(apiPackage, customPropertyCount)
                : null;

              return (
                <div
                  key={apiPackage._id}
                  className="w-full cursor-pointer rounded-xl bg-white group hover:bg-themeColor transition-all duration-600 transform hover:scale-y-105 hover:shadow-lg border border-gray-200 flex flex-col h-full ease delay-75"
                >
                  <div className="flex-1 flex flex-col mt-6 w-[90%] mx-auto">
                    <div className="border-b-2 border-darkColor/20 pb-6 group-hover:border-white">
                      <ParagraphTag
                        content={displayPackage.package}
                        classes="text-themeColor font-popinsMedium text-base group-hover:text-white transition-colors duration-300"
                      />
                      <ParagraphTag
                        content={displayPackage.durationLabel}
                        classes="text-darkColor font-popinsRegular text-sm group-hover:text-white/70 transition-colors duration-300"
                      />

                      {/* Properties allowed */}
                      <ParagraphTag
                        content={
                          isCustom
                            ? "Custom Properties"
                            : `Up to ${displayPackage.allowProperties} properties`
                        }
                        classes="text-themeColor font-popinsMedium text-sm group-hover:text-white transition-colors duration-300"
                      />

                      {/* Custom property input */}
                      {isCustom && (
                        <div className="mt-3">
                          <label className="text-darkColor font-popinsRegular text-xs group-hover:text-white/80 transition-colors duration-300 block mb-1">
                            Number of Properties:
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={customPropertyCount || ""}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setCustomProperties((prev) => ({
                                ...prev,
                                [apiPackage._id]: value,
                              }));
                            }}
                            placeholder="Enter number of properties"
                            className="w-full mt-1 text-sm font-popinsRegular bg-bgColor px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50"
                          />

                          {/* Discount information */}
                          {customPropertyCount > 0 && (
                            <div className="mt-2 space-y-1">
                              {customPricing?.discountPercentage > 0 && (
                                <ParagraphTag
                                  content={`${customPricing.discountPercentage}% discount (
                                  ${customPricing.discountAmount} ) applied!`}
                                  classes="text-green-600 pl-2 font-popinsRegular text-xs group-hover:text-green-200 transition-colors duration-300 leading-relaxed"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <ParagraphTag
                        content={
                          isCustom
                            ? customPricing?.totalPrice || "$0"
                            : displayPackage.totalPrice
                        }
                        classes="text-black font-popinsSemiBold text-2xl group-hover:text-white transition-colors duration-300 mt-2"
                      />
                      {/* Show original price with strikethrough if there's a discount */}
                      {isCustom && customPricing?.discountPercentage > 0 && (
                        <ParagraphTag
                          content={`Originally: ${customPricing.originalPrice}`}
                          classes="text-gray-500 font-popinsRegular text-sm line-through group-hover:text-white/60 transition-colors duration-300"
                        />
                      )}
                      <ParagraphTag
                        content={`${
                          isCustom
                            ? customPricing?.monthlyPrice || "$0"
                            : displayPackage.monthlyPrice
                        } /month`}
                        classes="text-darkColor/60 font-popinsRegular text-xs group-hover:text-white/80 transition-colors duration-300"
                      />
                      {displayPackage.savePerYear && !isCustom && (
                        <ParagraphTag
                          content={`Save ${displayPackage.savePerYear} per year`}
                          classes="text-green-600 font-popinsMedium text-xs group-hover:text-green-200 transition-colors duration-300"
                        />
                      )}
                      <ParagraphTag
                        content={displayPackage.description}
                        classes="text-darkColor/40 pt-2 font-popinsRegular text-xs group-hover:text-white/70 transition-colors duration-300"
                      />
                    </div>

                    <div className="w-full flex-1">
                      {displayPackage.features.map((feature, j) => (
                        <div key={j} className="mt-3 flex items-start">
                          <div className="w-4 h-4 flex justify-center items-center rounded-full bg-themeColor group-hover:bg-white transition-colors duration-300 mt-0.5 flex-shrink-0">
                            <Check
                              size={12}
                              className="text-white group-hover:text-themeColor transition-colors duration-300"
                            />
                          </div>
                          <ParagraphTag
                            content={feature}
                            classes="text-darkColor pl-2 font-popinsRegular text-xs group-hover:text-white transition-colors duration-300 leading-relaxed"
                          />
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto pt-6 pb-6">
                      {canSelectPlan() &&
                        (purchaseLoadingId === apiPackage._id ? (
                          <div className="flex w-full justify-center items-center py-2">
                            <MyLoader />
                          </div>
                        ) : (
                          <button
                            onClick={() =>
                              setConfirmPackage({ apiPackage, displayPackage, customPropertyCount, customPricing })
                            }
                            disabled={
                              !!purchaseLoadingId ||
                              (isCustom && (!customPropertyCount || customPropertyCount <= 0))
                            }
                            className={`w-full h-10 text-white rounded-md transition-colors duration-300 font-popinsMedium ${
                              !!purchaseLoadingId ||
                              (isCustom && (!customPropertyCount || customPropertyCount <= 0))
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-themeColor group-hover:bg-white group-hover:text-themeColor"
                            }`}
                          >
                            Select {displayPackage.package}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

          {/* Confirmation overlay */}
          {confirmPackage && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 w-full max-w-sm mx-auto">
                <p className="text-darkColor font-popinsSemiBold text-lg text-center mb-1">
                  Confirm Subscription
                </p>
                <p className="text-paraColor font-popinsRegular text-xs text-center mb-6">
                  You're about to activate the following plan
                </p>

                <div className="bg-bgColor rounded-xl p-5 mb-6">
                  <p className="text-themeColor font-popinsSemiBold text-base">
                    {confirmPackage.displayPackage.package}
                  </p>
                  <p className="text-darkColor/60 font-popinsRegular text-xs mt-0.5">
                    {confirmPackage.displayPackage.durationLabel}
                  </p>
                  <p className="text-darkColor font-popinsSemiBold text-2xl mt-3">
                    {confirmPackage.displayPackage.isCustom
                      ? confirmPackage.customPricing?.totalPrice || "$0"
                      : confirmPackage.displayPackage.totalPrice}
                  </p>
                  <p className="text-darkColor/50 font-popinsRegular text-xs">
                    {confirmPackage.displayPackage.isCustom
                      ? `${confirmPackage.customPricing?.monthlyPrice || "$0"} /month`
                      : `${confirmPackage.displayPackage.monthlyPrice} /month`}
                  </p>
                  {!confirmPackage.displayPackage.isCustom && confirmPackage.displayPackage.savePerYear && (
                    <p className="text-green-600 font-popinsMedium text-xs mt-1">
                      Save {confirmPackage.displayPackage.savePerYear} per year
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  {purchaseLoadingId ? (
                    <div className="flex justify-center items-center py-2">
                      <MyLoader />
                    </div>
                  ) : (
                    <button
                      onClick={() => buySubscription(confirmPackage.apiPackage._id, confirmPackage.displayPackage.package)}
                      className="w-full h-10 bg-themeColor text-white rounded-md font-popinsMedium text-sm"
                    >
                      Confirm &amp; Pay
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmPackage(null)}
                    disabled={!!purchaseLoadingId}
                    className="w-full h-10 border border-darkColor/20 text-darkColor/70 rounded-md font-popinsMedium text-sm disabled:opacity-40"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlan;
